import React, { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import { TabPill } from './styles';
import { AreaConfiguration, PoisConfiguration, CofConfiguration } from './RiskConfiguration';
import { useSetAtom } from 'jotai';
import { riskConfigData } from './state';
import { stripTypenameFromRemediationConfig, useSnackBarMessage } from './utils';
import { Button } from '@mui/material';
import {
  TemplateTypeEnum,
  useGetPaintBlockRemediationScoreLazyQuery,
  useRecalculateRiskRemediationAnalyticsMutation,
} from '@/__generated__/graphql';

const RiskConfigurationCategories = [
  {
    name: 'Area',
    value: 'area',
  },
  {
    name: 'POIs',
    value: 'pois',
  },
  {
    name: 'CoF',
    value: 'cof',
  },
];

// Component mapping
const categoryComponents = {
  area: AreaConfiguration,
  pois: PoisConfiguration,
  cof: CofConfiguration,
};

export const RiskConfigurationTemplate = ({ structureId }: { structureId: string }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('area');
  const setRiskConfig = useSetAtom(riskConfigData);
  const [getPaintBlockRemediationScore] = useGetPaintBlockRemediationScoreLazyQuery();
  const [recalculateRiskRemediationAnalytics, { loading }] =
    useRecalculateRiskRemediationAnalyticsMutation();
  const [templateId, setTemplateId] = useState<string | null>(null);
  const { showSnackBar } = useSnackBarMessage({});

  useEffect(() => {
    if (structureId) {
      getPaintBlockRemediationScore({
        variables: {
          structureId,
          type: TemplateTypeEnum.PaintBlocks,
        },
        fetchPolicy: 'cache-first',
      }).then((response) => {
        const remediationScoreConfig = response.data?.allTemplates[0].remediationScoreConfig;
        if (remediationScoreConfig) {
          const cleanConfig = stripTypenameFromRemediationConfig(remediationScoreConfig);
          setRiskConfig(cleanConfig);
        }
        const selectedTemplateId = response.data?.allTemplates[0].id;
        if (selectedTemplateId) setTemplateId(selectedTemplateId);
      });
    }
  }, [structureId, getPaintBlockRemediationScore, setRiskConfig]);

  const handleRecalculateRisk = () => {
    recalculateRiskRemediationAnalytics({
      variables: {
        input: {
          structureId,
        },
      },
    });

    showSnackBar(
      'Recalculation is running in the background - you can keep working without interruption.',
      'info'
    );
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
  };

  const SelectedComponent = categoryComponents[selectedCategory as keyof typeof categoryComponents];

  return (
    <Stack spacing={3}>
      <Stack direction="row" gap={2} justifyContent="space-between" pt={2}>
        <Stack direction="row" gap={2} alignItems="center">
          {RiskConfigurationCategories.map((category) => (
            <TabPill
              key={category.value}
              variant="text"
              size="small"
              selected={selectedCategory === category.value}
              onClick={() => handleCategorySelect(category.value)}
            >
              {category.name}
            </TabPill>
          ))}
        </Stack>
        <Button variant="contained" onClick={handleRecalculateRisk} disabled={loading}>
          {loading ? 'Recalculating...' : 'Recalculate Risk'}
        </Button>
      </Stack>

      <>
        {SelectedComponent && templateId && (
          <SelectedComponent structureId={structureId} templateId={templateId} />
        )}
      </>
    </Stack>
  );
};
